import { type Ref, ref, watch, provide } from 'vue';
import type { TabTransitionDirection } from 'src/design_system/components/tab/types';
import {
  MERIDIA_TABS_ORDER,
  MERIDIA_TABS_REGISTER_TAB,
  MERIDIA_TABS_UNREGISTER_TAB,
  MERIDIA_TABS_TRANSITION_DIRECTION,
} from 'src/design_system/components/tab/types';

export function useTabTransition(modelValue: Ref<string | number>): void {
  const tabOrder = ref<(string | number)[]>([]);

  /**
   * Registers a value for tab-order tracking. Order follows mount order — the
   * sequence in which MeridiaTabPanel children call registerTab. Panels are the
   * single source of order because they are always present (headers are optional).
   *
   * Note: a MeridiaTabPanel wrapped in v-if that re-mounts is appended at the end of
   * the order, not its original position, which can skew transition direction. Prefer
   * v-show over v-if when toggling panels.
   */
  function registerTab(value: string | number): void {
    if (!tabOrder.value.includes(value)) {
      tabOrder.value.push(value);
    }
  }

  function unregisterTab(value: string | number): void {
    const index = tabOrder.value.indexOf(value);
    if (index !== -1) {
      tabOrder.value.splice(index, 1);
    }
  }

  const transitionDirection = ref<TabTransitionDirection>('none');

  watch(
    () => modelValue.value,
    (newValue, oldValue) => {
      if (newValue == null || oldValue == null) {
        transitionDirection.value = 'none';
        return;
      }

      const newIndex = tabOrder.value.indexOf(newValue);
      const oldIndex = tabOrder.value.indexOf(oldValue);

      if (newIndex === -1 || oldIndex === -1) {
        transitionDirection.value = 'none';
        return;
      }

      transitionDirection.value = newIndex > oldIndex ? 'forward' : 'backward';
    }
  );

  provide(MERIDIA_TABS_ORDER, tabOrder);
  provide(MERIDIA_TABS_REGISTER_TAB, registerTab);
  provide(MERIDIA_TABS_UNREGISTER_TAB, unregisterTab);
  provide(MERIDIA_TABS_TRANSITION_DIRECTION, transitionDirection);
}
